import React from 'react'
import { Button, Table, Tag, Typography, Upload } from 'antd'
import {
  CloseOutlined,
  DownloadOutlined,
  ExportOutlined,
  FileExcelOutlined,
  ImportOutlined,
  PlayCircleOutlined,
  UploadOutlined
} from '@ant-design/icons'
import moment from 'moment'
import { sellerApi } from 'api/seller'
import {
  IImportExcelHistoryItem,
  IImportExcelHistoryResponse,
  ILastExcelStockUpdateResponse
} from 'models/seller'
import { IMAGES_URL } from 'shared/constants/urls'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { downloadBlobResponse } from 'shared/utils/downloadBlobResponse'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { StockConfirmationStatus } from 'components/sellers/StockConfirmationStatus/StockConfirmationStatus'
import { SectionTitle } from 'components/ui'
import styles from './SellerMassImport.module.scss'
import type { ColumnsType } from 'antd/es/table'

interface ISellerMassImportProps {
  sellerId: string
  showLastStockUpdateDate?: boolean
  useAdminStockConfirmation?: boolean
}

interface IHistoryStatusMeta {
  color: string
  label: string
}

const PAGE_SIZE = 10
const MAX_FILE_SIZE = 10 * 1024 * 1024
const FILE_NAME_PREVIEW_LENGTH = 12
const MASS_IMPORT_INSTRUCTION_URL =
  'https://bookstack-production-3d81.up.railway.app/books/horsesmart-dlia-prodavca-sinxronizaciia-tovarov/page/instrukciia-po-sinxronizacii-tovarov-cerez-excel-v-horsesmart'
const MASS_IMPORT_VIDEO_INSTRUCTION_URL =
  'https://rutube.ru/video/private/d9851988ad99ec2f89d5888ea8c7cd63/?p=bXzEkMw08qNeUbo5ZHKXRg'

const getHistoryErrorsCount = (item: IImportExcelHistoryItem): number => {
  return Number(item.errorRows ?? 0)
}

const getHistoryStatusMeta = (
  item: IImportExcelHistoryItem
): IHistoryStatusMeta => {
  const errorsCount = getHistoryErrorsCount(item)

  if (errorsCount > 0) {
    return {
      color: 'error',
      label: 'Есть ошибки'
    }
  }

  return {
    color: 'success',
    label: 'Готов'
  }
}

const getHistoryDate = (item: IImportExcelHistoryItem): string => {
  const rawDate = item.createdAt ?? item.created_at
  if (!rawDate) {
    return '-'
  }

  return moment(rawDate).format('DD.MM.YYYY HH:mm:ss')
}

const formatLastStockUpdateDate = (value: string | null): string => {
  if (!value) {
    return '-'
  }

  const parsedDate = moment(value)
  if (!parsedDate.isValid()) {
    return value
  }

  return parsedDate.format('DD.MM.YYYY HH:mm:ss')
}

const getHistoryReportUrl = (item: IImportExcelHistoryItem): string => {
  const rawUrl = item.reportUrl ?? item.report_url ?? item.excelFile ?? ''
  if (!rawUrl) {
    return ''
  }

  if (/^https?:\/\//i.test(rawUrl)) {
    return rawUrl
  }

  const normalizedBase = IMAGES_URL.endsWith('/') ? IMAGES_URL : `${IMAGES_URL}/`
  const normalizedPath = rawUrl.startsWith('/') ? rawUrl.slice(1) : rawUrl
  return `${normalizedBase}${normalizedPath}`
}

const getHistoryFileName = (item: IImportExcelHistoryItem): string => {
  return item.fileName ?? item.file_name ?? item.originalName ?? item.name ?? '-'
}

const getShortFileName = (value: string): string => {
  if (value.length <= FILE_NAME_PREVIEW_LENGTH) {
    return value
  }

  return `${value.slice(0, FILE_NAME_PREVIEW_LENGTH)}...`
}

const getHistoryResult = (item: IImportExcelHistoryItem): string => {
  if (item.result) {
    return item.result
  }

  const updated = Number(
    item.updatedCount ??
    item.updated_count ??
    item.importedCount ??
    item.imported_count ??
    item.updateRows ??
    0
  )
  const errors = getHistoryErrorsCount(item)

  if (updated === 0 && errors === 0) {
    return 'Без ошибок'
  }

  if (errors > 0) {
    return `Обновлено ${updated}, Ошибок ${errors}`
  }

  return `Обновлено ${updated}`
}

export const SellerMassImport: React.FC<ISellerMassImportProps> = ({
  sellerId,
  showLastStockUpdateDate = true,
  useAdminStockConfirmation = false
}) => {
  const isMobile = useIsMobile()

  const [historyItems, setHistoryItems] = React.useState<IImportExcelHistoryItem[]>(
    []
  )
  const [historyTotal, setHistoryTotal] = React.useState(0)
  const [historyPage, setHistoryPage] = React.useState(1)
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(false)
  const [lastStockUpdateDate, setLastStockUpdateDate] = React.useState<string | null>(null)
  const [isLoadingLastStockUpdateDate, setIsLoadingLastStockUpdateDate] = React.useState(false)

  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [isExportingProducts, setIsExportingProducts] = React.useState(false)
  const [isImporting, setIsImporting] = React.useState(false)

  const getHistory = React.useCallback(
    async (nextPage = historyPage) => {
      try {
        setIsLoadingHistory(true)
        const response = await sellerApi.getExcelImportHistory({
          skip: String((nextPage - 1) * PAGE_SIZE),
          limit: String(PAGE_SIZE),
          seller_id: sellerId
        })

        const responseData = response.data as
          | IImportExcelHistoryResponse
          | IImportExcelHistoryItem[]
        const items = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData.data)
            ? responseData.data
            : responseData.items ?? []
        const total = Array.isArray(responseData)
          ? responseData.length
          : responseData.total ?? responseData.count ?? items.length

        setHistoryItems(items)
        setHistoryTotal(total)
        setHistoryPage(nextPage)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoadingHistory(false)
      }
    },
    [historyPage, sellerId]
  )

  const getLastStockUpdateDate = React.useCallback(async () => {
    try {
      setIsLoadingLastStockUpdateDate(true)
      const response = await sellerApi.getLastExcelStockUpdate(sellerId)
      const responseData = response.data as ILastExcelStockUpdateResponse
      setLastStockUpdateDate(responseData?.lastUpdatedAt ?? null)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoadingLastStockUpdateDate(false)
    }
  }, [sellerId])

  React.useEffect(() => {
    getHistory(1)
    if (showLastStockUpdateDate) {
      getLastStockUpdateDate()
    }
  }, [getHistory, getLastStockUpdateDate, showLastStockUpdateDate])

  const onExportProducts = async () => {
    try {
      setIsExportingProducts(true)
      const response = await sellerApi.exportSellersProductsToExcel(sellerId)
      downloadBlobResponse(response, 'products_export.xlsx')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsExportingProducts(false)
    }
  }

  const onImport = async () => {
    if (!selectedFile) {
      showMessage(
        'warning',
        'Файл не выбран',
        'Загрузите .xlsx файл перед запуском импорта.'
      )
      return
    }

    try {
      setIsImporting(true)
      await sellerApi.importSellersProducts(selectedFile, sellerId)
      setSelectedFile(null)
      await getHistory(1)
      if (showLastStockUpdateDate) {
        await getLastStockUpdateDate()
      }
    } catch (e) {
      showServerError(e)
    } finally {
      setIsImporting(false)
    }
  }

  const onDownloadErrorReport = (item: IImportExcelHistoryItem) => {
    const reportUrl = getHistoryReportUrl(item)
    if (!reportUrl) {
      return
    }

    const link = document.createElement('a')
    link.href = reportUrl
    link.target = '_blank'
    link.rel = 'noreferrer'
    link.click()
  }

  const columns: ColumnsType<IImportExcelHistoryItem> = React.useMemo(
    () => [
      {
        title: 'Дата/время',
        dataIndex: 'createdAt',
        width: 140,
        render: (_, item) => (
          <Typography.Text className={styles.dateValue}>
            {getHistoryDate(item)}
          </Typography.Text>
        )
      },
      {
        title: 'Файл',
        dataIndex: 'fileName',
        width: 100,
        render: (_, item) => {
          const name = getHistoryFileName(item)
          return (
            <Typography.Text className={styles.fileName} title={name}>
              {getShortFileName(name)}
            </Typography.Text>
          )
        }
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: 100,
        render: (_, item) => {
          const statusMeta = getHistoryStatusMeta(item)
          return <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
        }
      },
      {
        title: 'Итог',
        dataIndex: 'result',
        width: 120,
        render: (_, item) => (
          <Typography.Text className={styles.resultValue}>
            {getHistoryResult(item)}
          </Typography.Text>
        )
      },
      {
        title: 'Отчет об ошибках',
        dataIndex: 'report',
        width: 140,
        render: (_, item) => {
          const reportUrl = getHistoryReportUrl(item)
          return (
            <Button
              icon={<DownloadOutlined />}
              onClick={() => onDownloadErrorReport(item)}
              disabled={!reportUrl}
            >
              Скачать
            </Button>
          )
        }
      }
    ],
    []
  )

  // Рендеры мобильных колонок берём из десктопных, чтобы правки не расходились
  const mobileColumns: ColumnsType<IImportExcelHistoryItem> = React.useMemo(() => {
    const [dateColumn, fileColumn, statusColumn, resultColumn] = columns

    return [
      {
        title: 'Импорт',
        dataIndex: 'createdAt',
        width: '62%',
        render: (value, item, index) => (
          <div className={styles.mobileCell}>
            <span className={styles.mobileDate}>
              {dateColumn?.render?.(value, item, index) as React.ReactNode}
            </span>
            <span className={styles.mobileSubText}>
              {fileColumn?.render?.(value, item, index) as React.ReactNode}
            </span>
            <span className={styles.mobileSubText}>
              {resultColumn?.render?.(value, item, index) as React.ReactNode}
            </span>
          </div>
        )
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        width: '38%',
        render: (value, item, index) => (
          <div className={styles.mobileStatusCell}>
            {statusColumn?.render?.(value, item, index) as React.ReactNode}
            <Button
              icon={<DownloadOutlined />}
              size='small'
              onClick={() => onDownloadErrorReport(item)}
              disabled={!getHistoryReportUrl(item)}
            />
          </div>
        )
      }
    ]
  }, [columns])

  return (
    <div className={styles.root}>
      <SectionTitle level={2} className={styles.title}>
        Массовый импорт
      </SectionTitle>
      <StockConfirmationStatus
        sellerId={useAdminStockConfirmation ? sellerId : undefined}
      />
      {showLastStockUpdateDate && (
        <Typography.Text className={styles.lastUpdate}>
          Последний Excel-импорт:{' '}
          <span className={styles.lastUpdateValue}>
            {isLoadingLastStockUpdateDate
              ? 'Загрузка...'
              : formatLastStockUpdateDate(lastStockUpdateDate)}
          </span>
        </Typography.Text>
      )}
      <Typography.Text className={styles.description}>
        Загрузите Excel-файл для создания или обновления товаров.
      </Typography.Text>

      <section className={`${styles.block} ${styles.stepBlock}`}>
        <Typography.Title level={3} className={styles.blockTitle}>
          Шаг 1. Экспорт товаров
        </Typography.Title>
        <div className={styles.stepRow}>
          <Button
            icon={<ExportOutlined />}
            onClick={onExportProducts}
            loading={isExportingProducts}
          >
            Экспортировать
          </Button>
          <Typography.Text className={styles.inlineText}>
            Скачайте актуальный список всех товаров в Excel.
          </Typography.Text>
          <Typography.Link
            href={MASS_IMPORT_INSTRUCTION_URL}
            target='_blank'
            rel='noreferrer'
          >
            Инструкция
          </Typography.Link>
          <Button
            type='primary'
            icon={<PlayCircleOutlined />}
            href={MASS_IMPORT_VIDEO_INSTRUCTION_URL}
            target='_blank'
            rel='noreferrer'
            className={styles.videoInstructionButton}
          >
            ВИДЕО-ИНСТРУКЦИЯ
          </Button>
        </div>
      </section>

      <section className={`${styles.block} ${styles.stepBlock}`}>
        <Typography.Title level={3} className={styles.blockTitle}>
          Шаг 2. Загрузка файла
        </Typography.Title>
        <div className={styles.uploadRow}>
          <div className={styles.uploadControls}>
            <Upload
              accept='.xlsx'
              multiple={false}
              maxCount={1}
              disabled={Boolean(selectedFile)}
              showUploadList={false}
              beforeUpload={(file) => {
                if (selectedFile) {
                  showMessage(
                    'warning',
                    'Файл уже загружен',
                    'Удалите текущий файл, чтобы выбрать другой.'
                  )
                  return Upload.LIST_IGNORE
                }

                const isXlsx = file.name.toLowerCase().endsWith('.xlsx')
                if (!isXlsx) {
                  showMessage(
                    'error',
                    'Неверный формат',
                    'Поддерживается только .xlsx файл.'
                  )
                  return Upload.LIST_IGNORE
                }

                if (file.size > MAX_FILE_SIZE) {
                  showMessage(
                    'error',
                    'Файл слишком большой',
                    'Максимальный размер файла — 10 Мб.'
                  )
                  return Upload.LIST_IGNORE
                }

                setSelectedFile(file as File)
                return false
              }}
            >
              <Button
                icon={<UploadOutlined />}
                disabled={Boolean(selectedFile)}
              >
                Загрузите файл
              </Button>
            </Upload>
            <Typography.Text className={styles.supportText}>
              Поддерживается: .xlsx(Excel), до 10 Мб
            </Typography.Text>
            {selectedFile && (
              <div className={styles.selectedFile}>
                <FileExcelOutlined />
                <Typography.Text>{selectedFile.name}</Typography.Text>
                <button
                  type='button'
                  className={styles.removeFileButton}
                  onClick={() => setSelectedFile(null)}
                  aria-label='Удалить выбранный файл'
                >
                  <CloseOutlined />
                </button>
              </div>
            )}
          </div>
          <div className={styles.uploadHint}>
            <Typography.Text>
              Система обновляет только заполненные поля, прошедшие проверку.
            </Typography.Text>
            <Typography.Text>
              Пустые поля игнорируются. Ошибки будут указаны в отчете.
            </Typography.Text>
          </div>
        </div>
      </section>

      <section className={`${styles.block} ${styles.stepBlock}`}>
        <Typography.Title level={3} className={styles.blockTitle}>
          Шаг 3. Запустить импорт
        </Typography.Title>
        <div className={styles.stepRow}>
          <Button
            type='primary'
            icon={<ImportOutlined />}
            loading={isImporting}
            onClick={onImport}
            disabled={!selectedFile}
          >
            Импортировать
          </Button>
          <Typography.Text className={styles.inlineText}>
            Результаты появятся ниже.
          </Typography.Text>
        </div>
      </section>

      <Typography.Title level={2} className={styles.historyTitle}>
        История импортов
      </Typography.Title>
      <Table
        className={styles.historyTable}
        columns={isMobile ? mobileColumns : columns}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        dataSource={historyItems}
        scroll={isMobile ? undefined : { x: 640 }}
        rowKey={(item) =>
          item._id ?? item.id ?? `${item.fileName ?? item.name}-${item.createdAt}`
        }
        loading={isLoadingHistory}
        pagination={{
          current: historyPage,
          total: historyTotal,
          pageSize: PAGE_SIZE,
          onChange: (page) => getHistory(page),
          showSizeChanger: false,
          hideOnSinglePage: true
        }}
      />
    </div>
  )
}
